// Helpers calendrier PWA - identique a la version web (RapteckApp/src/utils/calendarHelpers.js)
// pour garantir des couleurs coherentes web <-> mobile pour un meme inspecteur.

const DEFAULT_DUREE_MINUTES = 180;

const FALLBACK_PALETTE = [
  '#1a73e8', '#16a765', '#7986cb', '#f4511e', '#33b679', '#8e24aa',
  '#e67c73', '#039be5', '#3f51b5', '#0b8043', '#d50000', '#f6bf26',
];

const hashString = (str) => {
  let hash = 5381;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const isValidHex = (value) => typeof value === 'string' && /^#?[0-9a-fA-F]{6}$/.test(value.trim());

const normalizeHex = (value) => {
  const v = String(value).trim();
  return v.startsWith('#') ? v : `#${v}`;
};

export const getInspecteurColor = (inspecteur) => {
  if (!inspecteur) return FALLBACK_PALETTE[0];
  if (isValidHex(inspecteur.couleur_calendrier)) return normalizeHex(inspecteur.couleur_calendrier);
  if (isValidHex(inspecteur.couleurCalendrier)) return normalizeHex(inspecteur.couleurCalendrier);
  const seed = inspecteur.id || `${inspecteur.prenom || ''}${inspecteur.nom || ''}`;
  return FALLBACK_PALETTE[hashString(seed) % FALLBACK_PALETTE.length];
};

export const getColorForInspecteurId = (inspecteurId, inspecteursById) => {
  if (!inspecteurId) return '#94a3b8';
  if (inspecteursById && inspecteursById[inspecteurId]) {
    return getInspecteurColor(inspecteursById[inspecteurId]);
  }
  return FALLBACK_PALETTE[hashString(inspecteurId) % FALLBACK_PALETTE.length];
};

/**
 * Calcule la couleur a partir d'une mission (utilise inspecteur_couleur_calendrier
 * si fourni par le backend, sinon hash sur inspecteur_id).
 */
export const getMissionColor = (mission) => {
  if (!mission) return '#94a3b8';
  if (isValidHex(mission.inspecteur_couleur_calendrier)) {
    return normalizeHex(mission.inspecteur_couleur_calendrier);
  }
  if (isValidHex(mission.inspecteur?.couleurCalendrier)) {
    return normalizeHex(mission.inspecteur.couleurCalendrier);
  }
  if (mission.inspecteur_id) {
    return FALLBACK_PALETTE[hashString(mission.inspecteur_id) % FALLBACK_PALETTE.length];
  }
  return '#94a3b8';
};

export const getReadableTextColor = (hex) => {
  if (!isValidHex(hex)) return '#ffffff';
  const v = normalizeHex(hex).slice(1);
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? '#1f2937' : '#ffffff';
};

/**
 * Retourne { startMinutes, endMinutes, dureeMinutes } pour une mission.
 * - startMinutes : minutes ecoulees depuis 00:00 du jour
 * - endMinutes : startMinutes + dureeMinutes (clipped a 24*60)
 * - Utilise heure_debut/heure_fin si fournis, sinon date_prevue + duree_minutes (fallback DEFAULT)
 */
export const getMissionMinuteRange = (mission) => {
  if (!mission) return null;

  const parseHHmm = (hhmm) => {
    if (typeof hhmm !== 'string') return null;
    const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return h * 60 + min;
  };

  let startMinutes = parseHHmm(mission.heure_debut);
  if (startMinutes === null && mission.date_prevue) {
    const d = new Date(mission.date_prevue);
    if (!Number.isNaN(d.getTime())) {
      startMinutes = d.getHours() * 60 + d.getMinutes();
    }
  }
  if (startMinutes === null) return null;

  let endMinutes = parseHHmm(mission.heure_fin);
  let dureeMinutes = Number.isFinite(mission.duree_minutes) && mission.duree_minutes > 0
    ? mission.duree_minutes
    : null;

  if (endMinutes === null) {
    if (dureeMinutes === null) dureeMinutes = DEFAULT_DUREE_MINUTES;
    endMinutes = startMinutes + dureeMinutes;
  } else if (dureeMinutes === null) {
    dureeMinutes = Math.max(15, endMinutes - startMinutes);
  }

  if (endMinutes <= startMinutes) {
    endMinutes = startMinutes + (dureeMinutes || DEFAULT_DUREE_MINUTES);
  }
  endMinutes = Math.min(endMinutes, 24 * 60);

  return { startMinutes, endMinutes, dureeMinutes };
};

export const formatHHmmFromMinutes = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const ICONS_BY_ACTIVITE = {
  electricite: '⚡',
  levage: '🏗️',
  incendie: '🔥',
  thermographie: '🌡️',
  inspection_electrique: '⚡',
  inspection_generale: '📋',
  vgp: '🏗️',
};

export const getActiviteIcon = (activite) => ICONS_BY_ACTIVITE[activite] || '📋';

export const DEFAULT_MISSION_DUREE_MINUTES = DEFAULT_DUREE_MINUTES;
export const CALENDAR_FALLBACK_PALETTE = FALLBACK_PALETTE;
